/**
 * Real, linkable routes (`createBrowserRouter`).
 *
 * The guard is the interesting part. It reads the session through
 * `session.useSession()` — `useSyncExternalStore` underneath — so **clearing
 * the session actually re-renders**. A guard that reads `localStorage` during
 * render subscribes to nothing: the app would go on rendering while every
 * request 401s (SSO_INTEGRATION §3.4, the second of the three mistakes).
 */

import { SsoRedirect } from '@fiveox/shell'
import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet } from 'react-router'

import { App } from './App'
import { InvoiceDetail } from './routes/Billing/InvoiceDetail'
import { Invoices } from './routes/Billing/Invoices'
import { Ledger } from './routes/Billing/Ledger'
import { Receipts } from './routes/Billing/Receipts'
import { Retainers } from './routes/Billing/Retainers'
import { Compose } from './routes/Broadcasts/Compose'
import { Calendar } from './routes/Calendar'
import { Callback } from './routes/Callback'
import { CaseDetail } from './routes/Cases/CaseDetail'
import { Cases } from './routes/Cases/Queues'
import { Inbox } from './routes/Chat/Inbox'
import { Client360 } from './routes/Client360'
import { Comms } from './routes/Comms/Comms'
import { CaseDocuments } from './routes/Documents/CaseDocuments'
import { Leads } from './routes/CRM/Leads'
import { Proposals } from './routes/CRM/Proposals'
import { Clients } from './routes/Clients/List'
import { People } from './routes/Clients/People'
import { Integrations } from './routes/Firm/Integrations'
import { Onboarding } from './routes/Firm/Onboarding'
import { ImportJobDetail } from './routes/Imports/JobDetail'
import { Imports } from './routes/Imports'
import { NotFound, RouteError } from './routes/NotFound'
import { Overview } from './routes/Overview'
// w5g — reports (spec §M). `Report` is the one screen every report renders
// through; see routes/Reports/Report.tsx on why there are not seven.
import { Report } from './routes/Reports/Report'
import { Reports } from './routes/Reports'
import { Catalog } from './routes/Services/Catalog'
import { ServiceDetail } from './routes/Services/ServiceDetail'
import { Templates } from './routes/Services/Templates'
import { SiteBlog } from './routes/Site/Blog'
import { SiteBlogPost } from './routes/Site/BlogPost'
import { SiteHome } from './routes/Site'
import { SitePageEditor } from './routes/Site/PageEditor'
import { SitePages } from './routes/Site/Pages'
import { SiteMedia } from './routes/Site/Media'
import { SitePolicy } from './routes/Site/Policy'
import { SiteTemplates } from './routes/Site/Templates'
import { TemplateViewer } from './routes/Site/TemplateViewer'
import { SiteTheme } from './routes/Site/Theme'
import { SiteProvider } from './routes/Site/useSite'
import { SiteNavigation } from './routes/Site/Navigation'
// w19b — booking flows and bookings (C19, FR-J11, ADR-0037 §6).
import { BookingFlows } from './routes/Site/Booking/Flows'
import { SiteBookingFlow } from './routes/Site/Booking/FlowEditor'
// w19e — staff's See it for a draft (the owner's ruling of 2026-09-27, §10.11).
import { SiteBookingPreview } from './routes/Site/Booking/FlowPreview'
import { BookingRecord } from './routes/Money/Bookings/Detail'
import { Bookings } from './routes/Money/Bookings/List'
// w14a — the website owns what faces the visitor (C13, ADR-0032 §1): three
// pages moved here from Settings, as they were.
import { ClientSignIn } from './routes/Site/ClientSignIn'
import { DomainSettings } from './routes/Site/Domain'
import { SiteSocial } from './routes/Site/Social'
import { WorkflowEditor } from './routes/Workflows/Editor'
import { Workload } from './routes/Workload'
// w12a — settings as a hub, one page per area (C11, FR-A8).
import { Approvals as SettingsApprovals } from './routes/Firm/settings/Approvals'
import { BillingDefaults } from './routes/Firm/settings/Billing'
import { Branches as SettingsBranches } from './routes/Firm/settings/Branches'
import { ChecklistSettings } from './routes/Firm/settings/Checklist'
import { SettingsHub } from './routes/Firm/settings/Hub'
import { MOVED_ADDRESSES } from './routes/Firm/settings/cards'
import { FirmOrSocial } from './routes/Firm/settings/Moved'
// w12b — profession & website conduct (C11, FR-A8 §9, T-1.21).
import { ProfessionSettings } from './routes/Firm/settings/Profession'
import { RETURN_KEY, ssoConfig } from './lib/sso-config'
import { armSessionEnd } from './lib/session-end'
import { session } from './lib/session'

function RequireSession({ children }: { children: ReactNode }) {
  const current = session.useSession()

  // **A session ends everywhere it was trusted** (ADR-0017). Armed here because
  // this is the component that already owns the *no session → SSO* decision,
  // and it is mounted exactly once: the watchdog that asks auth whether this
  // session is still live, the scoped sign-out listener, and the firm clear.
  //
  // Nothing in it navigates. Ending the session re-renders this guard into
  // `SsoRedirect` below, which is the authoritative path.
  useEffect(armSessionEnd, [])

  // No session — hand the browser to accounts. **This app has no login screen**
  // (ADR-0048); `SsoRedirect` stashes the deep link and starts the PKCE flow.
  if (!current) {
    return <SsoRedirect config={ssoConfig()} returnKey={RETURN_KEY} />
  }
  return <>{children}</>
}

function Shell() {
  return (
    <RequireSession>
      <App>
        <Outlet />
      </App>
    </RequireSession>
  )
}

export const router = createBrowserRouter([
  // `/callback` sits **outside** the guard: it is the route that creates the
  // session, so guarding it would redirect the sign-in away from its own
  // completion.
  { path: '/callback', element: <Callback /> },
  {
    path: '/',
    element: <Shell />,
    // A screen that throws, or a thrown 404 — see `routes/NotFound.tsx`.
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Overview /> },
      { path: 'clients', element: <Clients /> },
      // w2a — clients and people (spec §B). **`:clientId` is last**: it is the
      // only parameterised path in this app so far, and a literal declared
      // after it would be swallowed by it. `/people` is the staff-side person
      // list and FR-B4's verification queue; it lives under routes/Clients/
      // because a person only exists in relation to a client.
      { path: 'people', element: <People /> },
      { path: 'clients/:clientId', element: <Client360 /> },
      // w1a — the firm (spec §A). Settings moved to w12a's block below.
      { path: 'onboarding', element: <Onboarding /> },
      // w2b — the catalog and the workflow editor (spec §C). `templates` is a
      // literal and must be declared **before** `:serviceId`, or it is read as
      // a service id and never reached.
      { path: 'services', element: <Catalog /> },
      { path: 'services/templates', element: <Templates /> },
      { path: 'services/:serviceId', element: <ServiceDetail /> },
      { path: 'services/:serviceId/workflow', element: <WorkflowEditor /> },
      // w3b — the compliance calendar (spec §D). One literal path with no
      // parameters, so no ordering question arises.
      { path: 'calendar', element: <Calendar /> },
      // w3a — the case core (spec §E). This **replaces the `/engagements`
      // seam**: ADR-0009 retired the word, and the unit of work is a case.
      // `:caseId` is last, for the same reason `:serviceId` is.
      { path: 'cases', element: <Cases /> },
      { path: 'cases/:caseId', element: <CaseDetail /> },
      // w4a — the workload screen (spec §L2). Its own literal path; nothing
      // above it is parameterised, so no ordering question arises.
      { path: 'workload', element: <Workload /> },
      // w4b — documents and chat (spec §F, §E8, §E9). A case's documents live
      // **under the case**, because that is where a firm looks for them; the
      // route is a sibling of 4A's `cases/:caseId` rather than a tab inside it,
      // so the two sessions of this wave own different files (waves.md §1.1).
      { path: 'cases/:caseId/documents', element: <CaseDocuments /> },
      { path: 'messages', element: <Inbox /> },
      { path: 'broadcasts', element: <Compose /> },
      // w4d — the website builder (spec §I). **One `SiteProvider` for the
      // whole group**, so the vocabulary — the nine block schemas, the six
      // templates, the fonts, the policy pack — is read once and every screen
      // under it draws its controls from the same answer. A per-screen read
      // would be five reads of shipped data and, worse, five chances for two
      // screens to be looking at different versions of it.
      //
      // `templates`, `pages`, `blog`, `policy` and `theme` are literals and
      // must stay above `pages/:pageId`, for the reason `services/templates`
      // does: a literal declared after a path parameter is read as a value of
      // it and never reached.
      {
        path: 'site',
        element: (
          <SiteProvider>
            <Outlet />
          </SiteProvider>
        ),
        children: [
          { index: true, element: <SiteHome /> },
          { path: 'templates', element: <SiteTemplates /> },
          { path: 'theme', element: <SiteTheme /> },
          { path: 'pages', element: <SitePages /> },
          { path: 'pages/:pageId', element: <SitePageEditor /> },
          { path: 'blog', element: <SiteBlog /> },
          // w14d — C13, §10.5 item 6: a post on its own edit page; `new` is the
          // one not yet saved (a value of the parameter, read by the page).
          { path: 'blog/:postId', element: <SiteBlogPost /> },
          { path: 'policy', element: <SitePolicy /> },
          // FR-I13 — the picture library. A literal, above nothing parameterised.
          { path: 'media', element: <SiteMedia /> },
          // w12c — FR-I15: the profession line and the menus. A literal.
          { path: 'navigation', element: <SiteNavigation /> },
          // w14a — moved here from Settings (C13, ADR-0032 §1). Literals; the
          // old `/settings/*` addresses redirect (below, in w12a's block).
          { path: 'client-sign-in', element: <ClientSignIn /> },
          { path: 'domain', element: <DomainSettings /> },
          { path: 'social', element: <SiteSocial /> },
          // w19b — C19, FR-J11 item 1. `booking` is a literal; `:flowId` below
          // it (`new` is the one not yet saved, a value of the parameter).
          { path: 'booking', element: <BookingFlows /> },
          { path: 'booking/:flowId', element: <SiteBookingFlow /> },
          { path: 'booking/:flowId/preview', element: <SiteBookingPreview /> },
        ],
      },
      // w4e — the CRM (spec §H). One literal path with no parameters; a lead
      // opens in a dialog on the list rather than at `/leads/:leadId`, because
      // the thing a firm does here is work a queue and a full-page route would
      // lose their place in it every time.
      { path: 'leads', element: <Leads /> },
      // w5e — proposals (spec §H4, §H5). Its own literal path beside `/leads`
      // rather than a tab inside it: a proposal has a life of its own after it
      // is sent — it is accepted, declined or it expires, by somebody who is
      // not in this app at all — and a firm looks for *"what have we quoted"*
      // without first choosing a lead. No parameterised sibling, so no ordering
      // question arises; one proposal opens in place on the list, as a lead does.
      { path: 'proposals', element: <Proposals /> },
      // w4c — billing (spec §G). **This replaces the `/invoices` seam.** The
      // paths live under `/billing` because the module is the client ledger and
      // not only its invoices — receipts, credit notes and the statement are
      // the same screen set. `:invoiceId` is last, for the same reason
      // `:caseId` is.
      { path: 'billing', element: <Invoices /> },
      { path: 'billing/receipts', element: <Receipts /> },
      { path: 'billing/ledger', element: <Ledger /> },
      // w5a — FR-G6's invoice schedules. Under `/billing` like the rest of
      // the module, and above the `:invoiceId` route for the reason the
      // service's own route file states about literal segments.
      { path: 'billing/retainers', element: <Retainers /> },
      { path: 'billing/invoices/:invoiceId', element: <InvoiceDetail /> },
      // w19b — Money → Bookings (C19, FR-J11 item 9). `:bookingId` last.
      { path: 'bookings', element: <Bookings /> },
      { path: 'bookings/:bookingId', element: <BookingRecord /> },
      // w5f — communication (spec §K). One literal path with four tabs inside
      // it, not four routes: a firm switching on a reminder is choosing a
      // template and an audience in the same minute, and three back-buttons
      // between them is how a rule ends up pointing at a template nobody read.
      { path: 'comms', element: <Comms /> },
      // w5g — reports (spec §M, §G8). `:key` is last, for the same reason
      // `:caseId` is: a literal declared after a path parameter is read as a
      // value of it and never reached.
      { path: 'reports', element: <Reports /> },
      { path: 'reports/:key', element: <Report /> },
      // w6a — imports (spec §N). `:jobId` last, for the reason `:caseId` and
      // `:key` above are: a literal declared after a path parameter is read as
      // a value of it and never reached.
      { path: 'imports', element: <Imports /> },
      { path: 'imports/:jobId', element: <ImportJobDetail /> },
      // w12a — Settings as a hub (C11, FR-A8). Every `/settings/*` page but
      // `/settings/profession`, which is 12B's. All literals, no parameters,
      // so no ordering question arises; `/settings/integrations` keeps the
      // address it has had since Wave 1, and an old `/settings#…` link is sent
      // on by the hub (`settings/cards.tsx: LEGACY_HASHES`).
      { path: 'settings', element: <SettingsHub /> },
      { path: 'settings/firm', element: <FirmOrSocial /> },
      { path: 'settings/billing', element: <BillingDefaults /> },
      { path: 'settings/approvals', element: <SettingsApprovals /> },
      { path: 'settings/branches', element: <SettingsBranches /> },
      // w14a — moved under the website (C13, ADR-0032 §4). The old addresses
      // answer with a redirect, so a bookmark or an older note still lands.
      { path: 'settings/integrations', element: <Integrations /> },
      ...MOVED_ADDRESSES.map(({ from, to }) => ({
        path: from.slice(1),
        element: <Navigate to={to} replace />,
      })),
      { path: 'settings/checklist', element: <ChecklistSettings /> },
      // w12b — the one `/settings/*` page 12A left for this session (its hub
      // card is already in `settings/cards.tsx`). A literal, like its siblings.
      { path: 'settings/profession', element: <ProfessionSettings /> },
      // A new route is one entry here and one `NAV` entry in `App.tsx`, with
      // the `needs` pair that matches it — docs/DOMAIN_HANDOFF.md §6.
      //
      // **Last, always.** Any path above wins over it; anything that matches
      // nothing lands here, inside the shell, instead of on React Router's
      // unstyled default. The server answers every unknown path with this
      // bundle and a 200, so this is the only place "not found" is ever said.
      { path: '*', element: <NotFound /> },
    ],
  },
  // w12d — the template viewer (FR-I16, T-4.56, I-7). **Outside `Shell`**, so
  // it is full-screen with no app chrome — it opens in its own tab from the
  // gallery's *Preview* — and **inside the session guard**, because it reads
  // the firm's templates and can apply one. A top-level sibling of `/`, not a
  // child: React Router ranks the static segments here above `/`'s `*`.
  {
    path: '/site/templates/:code/preview',
    element: (
      <RequireSession>
        <TemplateViewer />
      </RequireSession>
    ),
    errorElement: <RouteError />,
  },
])
